import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth, db } from "../../firebase/firebase";
import { notification } from "antd";
import { doc, setDoc, getDocs, Timestamp, collection, query, where } from "firebase/firestore";
import { RoleTypes } from "enum/role-type-enum";
import { SignUpResult, userCreateInterface } from "./dto";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { storage } from "utils/storage";
import dayjs from "dayjs";

const provider = new GoogleAuthProvider();

export const signUpWithGoogle = async (navigate: (path: string) => void): Promise<SignUpResult | void> => {
    try {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;

        
        if (!user?.providerData?.length || user.providerData[0]?.providerId?.trim() !== "google.com") {
            throw new Error("Your Email is registered, but not with a Google Account.");
        }

    
        const userInfosCollection = collection(db, "user_infos");
        const queryForUserInfo = query(userInfosCollection, where("uid", "==", user.uid));
        const querySnapshot = await getDocs(queryForUserInfo);

        
        if (!querySnapshot.empty) {
            return handleExistingUser(user, querySnapshot, navigate);
        }

    
        return createNewUser(user);
    } catch (error) {
        console.error("Google sign-in failed:", error);
        throw new Error("Google User is not registered.");
    }
};

const handleExistingUser = async (
    user: any,
    querySnapshot: any,
    navigate: (path: string) => void
): Promise<SignUpResult | void> => {
    const userDocSnap = querySnapshot.docs[0];
    const existingUserInfo = userDocSnap.data() as userCreateInterface;

    
    if (existingUserInfo.role && existingUserInfo.role !== RoleTypes.ENUMERATOR) {
        notification.error({
            message: "Registration Error",
            description: `You are already registered as ${existingUserInfo.role}. Please log in with the correct role.`,
        });
        return;
    }

    
    notification.info({
        message: "Already Registered",
        description: `You are already registered as an enumerator. Redirecting to your home page.`,
    });

    
    const idToken = await user.getIdToken();
    storage.setUserInStorage(existingUserInfo);
    storage.setTokenInStorage(idToken);

    navigate('/enumerator/home');
    return;
};

const createNewUser = async (user: any): Promise<SignUpResult> => {
    const userInfosCollection = collection(db, "user_infos");

    
    const userInfo: userCreateInterface = {
        email: user.email || "",
        role: RoleTypes.ENUMERATOR,
        uid: user.uid,
        firstName: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ")[1] || "",
        createdAt: Timestamp.fromDate(dayjs().toDate()),
        isVerified: true,
    };

    
    const userInfoDocRef = doc(userInfosCollection);
    await setDoc(userInfoDocRef, { ...userInfo, id: userInfoDocRef.id });

    
    const idToken = await user.getIdToken();
    return { userInfo, token: idToken };
};

export const useSignUpWithGoogle = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: signUpWithGoogle,
        onSuccess: async (data) => {
            if (!data) return;

            const { userInfo, token } = data;
            storage.setUserInStorage(userInfo);
            storage.setTokenInStorage(token);
            await queryClient.invalidateQueries({ queryKey: ["ENUMERATOR"] });

            notification.success({
                message: "Registration Successful",
                description: "You have successfully registered with Google.",
            });
        },
        onError: (error) => {
            notification.error({
                message: "Google Sign-Up Failed",
                description: error instanceof Error ? error.message : "An error occurred during Google sign-up.",
            });
        },
    });
};
